import { ExistingDestination } from "@prequel/react";

import axios, { ApiResponse, ApiRoutes } from "@/api/client";
import { handleApiError } from "@/api/helpers";
import { DataIntegrityJobDetails, GetDataIntegrityJobsResponse } from "@/types";

const getDataIntegrityJobs = async (
  destinationID: ExistingDestination["id"] | undefined,
): Promise<{
  destinationID: ExistingDestination["id"] | undefined;
  jobs: DataIntegrityJobDetails[];
  hasNext: boolean;
  nextUrl: string;
}> =>
  axios
    .get(`${ApiRoutes.DESTINATIONS}/${destinationID}/data-integrity`, {
      params: { count: 20 },
    })
    .then((response: ApiResponse<GetDataIntegrityJobsResponse>) => ({
      destinationID,
      jobs: response.data.data.data.jobs,
      hasNext: response.data.data.has_next,
      nextUrl: response.data.data.next_url,
    }))
    .catch(handleApiError("Failed to fetch data integrity jobs for destination.", true));

const getDataIntegrityJobByID: (
  destinationID: ExistingDestination["id"],
  jobID: DataIntegrityJobDetails["id"],
) => Promise<DataIntegrityJobDetails> = (destinationID, jobID) =>
  axios
    .get(`${ApiRoutes.DESTINATIONS}/${destinationID}/data-integrity/${jobID}`)
    .then((response: ApiResponse<{ job: DataIntegrityJobDetails }>) => {
      return response.data.data.job;
    })
    .catch(handleApiError("Failed to fetch data integrity job.", true));

const createDataIntegrityJob = async ({
  destinationID,
  modelNames,
  checkValueIntegrity = false,
}: {
  destinationID: ExistingDestination["id"];
  modelNames?: string[];
  checkValueIntegrity?: boolean;
}): Promise<{ destinationID: ExistingDestination["id"] }> =>
  axios
    .post(`${ApiRoutes.DESTINATIONS}/${destinationID}/data-integrity`, {
      models: modelNames?.map((name) => ({ model_name: name })),
      check_value_integrity: checkValueIntegrity,
    })
    .then(() => ({
      destinationID,
    }))
    .catch(handleApiError("Data integrity job failed to enqueue."));

const cancelDataIntegrityJob = async (jobID: string): Promise<void> =>
  axios
    .post(`${ApiRoutes.DESTINATIONS}/data-integrity/${jobID}/cancel`)
    .then(() => undefined)
    .catch(handleApiError("Data integrity job failed to cancel."));

const DataIntegrityService = {
  getDataIntegrityJobs,
  createDataIntegrityJob,
  cancelDataIntegrityJob,
  getDataIntegrityJobByID,
};

export default DataIntegrityService;
